<script setup lang="ts">
import { BModal } from 'bootstrap-vue';
import { computed, ref, watch } from 'vue';
import { getProductsWithSet } from '@/api/shopify/optionSets';
import { OptionSetDto } from '@extTypes/options/optionSet';
import { useStore } from '@/store';
import config from '@/config/config';

const store = useStore();
const userStore = computed(() => store.state.store);
const baseUrl = config.CUSTOMILY_BASE_URL;

const props = defineProps<{
    show: boolean;
    optionSet?: OptionSetDto;
}>();

const emit = defineEmits<{
    (e: 'close'): void;
}>();

const products = ref<
    {
        title: string;
        id: string;
        isDesign: boolean;
        imgSrc?: string;
        active: boolean;
        published: boolean;
        shop?: string;
    }[]
>([]);

const loading = ref(false);

watch(
    () => props.optionSet?.id,
    async (newId) => {
        if (!newId) return;
        loading.value = true;
        try {
            const data = await getProductsWithSet(newId);
            products.value = data;
        } catch (error) {
            console.error('Error retrieving products with sets:', error);
        } finally {
            loading.value = false;
        }
    },
    { immediate: true }
);

const isPublished = (product: (typeof products.value)[number]) => {
    return product.published === true;
};

const isActive = (product: (typeof products.value)[number]) => {
    return product.imgSrc?.includes('woo.customily') || product.imgSrc?.includes('etsystatic.com')
        ? product.active === true
        : false;
};

const getStatusLabel = (product: (typeof products.value)[number]) => {
    if (isPublished(product)) return 'Published';
    if (isActive(product)) return 'Active';
    return 'Draft';
};
</script>
<template>
    <b-modal id="option-set-linked-to-product-modal" :visible="show" centered size="lg" @hide="emit('close')">
        <template #modal-title>{{ optionSet?.title }}</template>

        <div v-if="loading" class="d-flex justify-content-center py-4">
            <b-spinner v-show="loading" small class="mr-2" />
        </div>

        <div v-else-if="!loading" class="px-1">
            <div class="pb-1">
                <h6 class="text-body font-weight-bold text-black mb-0">Linked products:</h6>
            </div>

            <div v-for="p in products" :key="p.id" class="d-flex flex-column my-3">
                <a
                    :href="`${baseUrl}/store/product?product=${p.id}&shop=${p.shop ?? userStore}`"
                    target="_blank"
                    class="d-flex justify-content-between cursor-pointer text-decoration-none"
                >
                    <!-- Columna izquierda -->
                    <div class="d-flex align-items-center">
                        <img
                            v-if="p.imgSrc"
                            :src="p.imgSrc"
                            :alt="p.title"
                            class="me-2"
                            style="width: 50px; height: 50px; object-fit: cover; border-radius: 4px"
                        />
                        <div class="d-flex">
                            <div class="align-self-center d-grid">
                                <h4 class="h6 mx-3 mb-0 font-weight-normal text-truncate text-black">{{ p.title }}</h4>
                            </div>
                            <div class="align-self-center">
                                <p
                                    class="mb-0 px-1 font-weight-normal"
                                    :class="{
                                        'color-published': isPublished(p),
                                        'color-active': isActive(p),
                                        'color-draft': !isPublished(p) && !isActive(p)
                                    }"
                                >
                                    {{ getStatusLabel(p) }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Columna derecha -->
                    <div class="d-flex align-items-center">
                        <font-awesome-icon icon="fa-solid fa-up-right-from-square" class="text-muted" />
                    </div>
                </a>
            </div>
        </div>

        <template #modal-footer>
            <div class="d-flex justify-content-end w-100">
                <b-button variant="primary" @click="emit('close')">Close</b-button>
            </div>
        </template>
    </b-modal>
</template>
<style scoped>
.color-published {
    color: #70ad64;
}

.color-draft {
    color: #808493;
}

.color-active {
    color: #4074fb;
}

.d-grid {
    display: grid !important;
    grid-auto-flow: column;
    align-items: center;
}

.text-black {
    color: #000 !important;
}
</style>
